import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { sortObjectOptions, sortStringOptions } from '@/utils/sortOptions';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from '../utils/toast';
import { Save, ArrowLeft, Eye, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailTemplateFormPage = () => {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const isEditMode = Boolean(templateId);
  
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [roleTypes, setRoleTypes] = useState([]);
  const [commonPlaceholders, setCommonPlaceholders] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState({ subject: '', body: '' });
  
  const [formData, setFormData] = useState({
    template_name: '',
    category: '',
    role_type: '',
    subject: '',
    body: '',
    description: '',
    placeholders: [],
    is_active: true
  });
  
  useEffect(() => {
    fetchMetadata();
    if (isEditMode) {
      fetchTemplate();
    }
  }, [templateId]);
  
  const fetchMetadata = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/email-templates/categories/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data.categories);
      setRoleTypes(response.data.role_types);
      setCommonPlaceholders(response.data.common_placeholders);
    } catch (error) {
      toast.error('Failed to fetch metadata');
    }
  };
  
  const fetchTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/email-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData(response.data);
    } catch (error) {
      toast.error('Failed to fetch template');
      navigate('/admin/email-templates');
    } finally {
      setLoading(false);
    }
  };
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const addPlaceholder = (placeholder) => {
    if (!formData.placeholders.includes(placeholder)) {
      setFormData(prev => ({
        ...prev,
        placeholders: [...prev.placeholders, placeholder]
      }));
    }
  };
  
  const removePlaceholder = (placeholder) => {
    setFormData(prev => ({
      ...prev,
      placeholders: prev.placeholders.filter(p => p !== placeholder)
    }));
  };
  
  const insertPlaceholder = (placeholder, field) => {
    const placeholderText = `{{${placeholder}}}`;
    setFormData(prev => ({
      ...prev,
      [field]: prev[field] + placeholderText
    }));
    addPlaceholder(placeholder);
  };
  
  const handlePreview = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Create sample data
      const sampleData = {
        user_name: 'John Doe',
        user_email: 'john.doe@example.com',
        hospital_name: 'Test Hospital',
        password: 'TempPass123',
        otp: '123456',
        institution_type: 'Multi-Specialty Clinic',
        department_name: 'Emergency',
        job_function: 'Nurse',
        phone_number: '+91 9876543210',
        plan_name: 'Advanced Plan',
        licenses: '100',
        expiry_date: '2025-12-31',
        activation_date: '2025-01-01',
        amount: '25000',
        transaction_id: 'TXN123456',
        invoice_no: 'INV-001',
        receipt_no: 'RCT-001'
      };
      
      const response = await axios.post(
        `${API}/admin/email-templates/preview`,
        {
          subject: formData.subject,
          body: formData.body,
          sample_data: sampleData
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setPreviewData(response.data);
      setShowPreview(true);
    } catch (error) {
      toast.error('Failed to generate preview');
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.template_name || !formData.category || !formData.role_type || !formData.subject || !formData.body) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      if (isEditMode) {
        await axios.put(
          `${API}/admin/email-templates/${templateId}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Template updated successfully');
      } else {
        await axios.post(
          `${API}/admin/email-templates`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Template created successfully');
      }
      
      navigate('/admin/email-templates');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="w-16 h-16 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout>
      <div className="p-8">
        {/* Page Title */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'EDIT EMAIL TEMPLATE' : 'CREATE EMAIL TEMPLATE'}
          </h1>
          <Button onClick={() => navigate('/admin/email-templates')} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
      
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Form */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="p-6">
                <h2 className="text-lg font-semibold mb-4" style={{ color: '#0056B3' }}>Template Details</h2>
                
                <div className="space-y-4">
                  <div>
                    <Label>Template Name *</Label>
                    <Input
                      value={formData.template_name}
                      onChange={(e) => handleChange('template_name', e.target.value)}
                      placeholder="e.g., Welcome Email for Hospital Admin"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Category *</Label>
                      <Select value={formData.category} onValueChange={(value) => handleChange('category', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {sortObjectOptions(categories, 'label').map(cat => (
                            <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label>Role Type *</Label>
                      <Select value={formData.role_type} onValueChange={(value) => handleChange('role_type', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select role type" />
                        </SelectTrigger>
                        <SelectContent>
                          {sortObjectOptions(roleTypes, 'label').map(role => (
                            <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Description</Label>
                    <Input
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      placeholder="Brief description of this template"
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => handleChange('is_active', e.target.checked)}
                      className="w-4 h-4"
                    />
                    <Label>Active Template</Label>
                  </div>
                </div>
              </Card>
              
              <Card className="p-6">
                <h2 className="text-lg font-semibold mb-4" style={{ color: '#0056B3' }}>Email Content</h2>
                
                <div className="space-y-4">
                  <div>
                    <Label>Subject Line *</Label>
                    <Input
                      value={formData.subject}
                      onChange={(e) => handleChange('subject', e.target.value)}
                      placeholder="e.g., Welcome to Eptral.Hospital – Your Account is Ready"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label>Email Body *</Label>
                    <textarea
                      value={formData.body}
                      onChange={(e) => handleChange('body', e.target.value)}
                      placeholder="Write your email content here. Use {{placeholder}} for dynamic content."
                      className="w-full min-h-[300px] p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0056B3]"
                      required
                    />
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <Button type="button" onClick={handlePreview} variant="outline">
                      <Eye className="w-4 h-4 mr-2" />
                      Preview
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
            
            {/* Sidebar - Placeholders */}
            <div className="space-y-6">
              <Card className="p-6">
                <h2 className="text-lg font-semibold mb-4" style={{ color: '#0056B3' }}>Placeholders</h2>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm">Available Placeholders</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {commonPlaceholders.map(placeholder => (
                        <Badge
                          key={placeholder}
                          className="cursor-pointer hover:bg-blue-100"
                          onClick={() => insertPlaceholder(placeholder, 'body')}
                        >
                          {placeholder}
                        </Badge>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Click to insert into body
                    </p>
                  </div>
                  
                  {formData.placeholders.length > 0 && (
                    <div>
                      <Label className="text-sm">Used Placeholders</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {formData.placeholders.map(placeholder => (
                          <Badge
                            key={placeholder}
                            variant="secondary"
                            className="flex items-center space-x-1"
                          >
                            <span>{placeholder}</span>
                            <X
                              className="w-3 h-3 cursor-pointer"
                              onClick={() => removePlaceholder(placeholder)}
                            />
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
              
              <Card className="p-6">
                <h2 className="text-lg font-semibold mb-4" style={{ color: '#0056B3' }}>Quick Guide</h2>
                <div className="text-sm space-y-2 text-gray-600">
                  <p>• Use placeholders like <code className="bg-gray-100 px-1 rounded">{'{{user_name}}'}</code></p>
                  <p>• Subject line should be clear and concise</p>
                  <p>• Keep body professional and branded</p>
                  <p>• Always preview before saving</p>
                </div>
              </Card>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              onClick={() => navigate('/admin/email-templates')}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#0056B3] hover:bg-[#004494]"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isEditMode ? 'Update Template' : 'Create Template'}
                </>
              )}
            </Button>
          </div>
        </form>
        
        {/* Preview Modal */}
        {showPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <Card className="max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-[18px] font-semibold" style={{ color: '#0056B3' }}>Email Preview</h2>
                <Button onClick={() => setShowPreview(false)} variant="ghost" size="sm">
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-semibold">Subject:</Label>
                  <p className="text-gray-800 mt-1">{previewData.preview_subject}</p>
                </div>
                
                <div className="border-t pt-4">
                  <Label className="text-sm font-semibold">Body:</Label>
                  <div className="mt-2 p-4 bg-gray-50 rounded-lg whitespace-pre-wrap">
                    {previewData.preview_body}
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default EmailTemplateFormPage;
